import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import mixpanel from './mixpanel';

type TimeTrackerProps = {
  userId: string;
  email: string;
  name: string;
};

const TimeTracker: React.FC<TimeTrackerProps> = ({ userId, email, name }) => {
  const location = useLocation();
  const startTimeRef = useRef(Date.now());
  const previousPathRef = useRef(location.pathname);
  const hasIdentified = useRef(false);

  // Identify user once
  useEffect(() => {
    if (!hasIdentified.current && userId) {
      mixpanel.identify(userId);
      mixpanel.people.set({ $email: email, $name: name });
      hasIdentified.current = true;
    }
  }, [userId, email, name]);

  // Track time on route change
  useEffect(() => {
    const now = Date.now();
    const timeSpent = (now - startTimeRef.current) / 1000;

    // Don't log the first time if there's no previous path
    if (previousPathRef.current && previousPathRef.current !== location.pathname) {
      mixpanel.track('Page View', {
        path: previousPathRef.current,
        time_spent_seconds: timeSpent,
        user_id: userId,
        email: email,
      });
    }

    // Reset timer
    previousPathRef.current = location.pathname;
    startTimeRef.current = now;
  }, [location.pathname]);

  // Track time when user closes tab
  useEffect(() => {
    const handleUnload = () => {
      const timeSpent = (Date.now() - startTimeRef.current) / 1000;

      mixpanel.track('Page View', {
        path: location.pathname,
        time_spent_seconds: timeSpent,
        user_id: userId,
        email: email,
      });
    };

    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload); // Safari support

    return () => {
      handleUnload();
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
    };
  }, [location.pathname]);

  return null;
};

export default TimeTracker;
